import { type PointerEvent } from 'react';

import { useTooltip } from '../../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data } from '../../../core/components/overlay-tooltip/types.js';
import { useTooltipReducer } from '../../../core/components/overlay-tooltip/useTooltipReducer.js';
import { emptyData } from '../../../core/components/overlay-tooltip/utils.js';
import { TOOLTIP_VARIANT } from '../constants.js';
import { useMultiMeterBarChartTooltipData } from '../context/MultiMeterBarChartTooltipContext.js';

export const useMultiMeterBarChartTooltipHandlers = (
  data: Data,
  setHoveredSegmentIndex: (index: number | undefined) => void,
) => {
  const tooltip = useTooltip();
  const dispatch = useTooltipReducer();
  const { tooltipConfig } = useMultiMeterBarChartTooltipData();

  const isSharedTooltip = tooltipConfig.variant === TOOLTIP_VARIANT.Shared;

  const buildSingleSeriesData = (segmentEl: Element) => {
    const index = Number(segmentEl.getAttribute('data-segment-index'));
    const series = [data.series[index]];
    return {
      geometry: 'multimeterbar' as const,
      series,
    };
  };

  const onClick = (event: PointerEvent<HTMLDivElement>) => {
    if (tooltip.getState().pinned) {
      dispatch({
        type: 'SET_PINNED',
        payload: {
          pinned: false,
          data: emptyData(),
        },
      })();
    } else {
      const { offsetX, offsetY } = event.nativeEvent;
      if (isSharedTooltip) {
        dispatch({
          type: 'SET_PINNED',
          payload: {
            pinned: true,
            relativeX: offsetX,
            relativeY: offsetY,
            data,
          },
        })();
      } else {
        const segmentEl = (event.target as Element).closest(
          '[data-segment-index]',
        );
        if (segmentEl) {
          dispatch({
            type: 'SET_PINNED',
            payload: {
              pinned: true,
              relativeX: offsetX,
              relativeY: offsetY,
              data: buildSingleSeriesData(segmentEl),
            },
          })();
        }
      }
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const segmentEl = (event.target as Element).closest('[data-segment-index]');
    if (isSharedTooltip) {
      if (!tooltip.getState().pinned) {
        setHoveredSegmentIndex(
          segmentEl
            ? Number(segmentEl.getAttribute('data-segment-index'))
            : undefined,
        );
      }
      dispatch({ type: 'SET_POSITION', payload: data })(event);
    } else if (segmentEl) {
      dispatch({
        type: 'SET_POSITION',
        payload: buildSingleSeriesData(segmentEl),
      })(event);
    } else {
      dispatch({ type: 'SET_OUT_BOUNDS' })();
    }
  };

  const onPointerLeave = () => {
    dispatch({ type: 'SET_OUT_BOUNDS' })();
  };

  return { onClick, onPointerMove, onPointerLeave };
};
